<script setup lang="ts">
import router from '@/router';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';

import { useBreadcrumbStore } from '@/stores/utils/breadcrumb.store';
import { isCurrentCrumb } from '@/common/utils/breadcrumb-trail.utils';

import { Breadcrumb, type BreadcrumbItem } from '@boqnet/ui';
import { BULK_ORDER_WIZARD_ROUTE_NAMES } from '@/stores/bulk-order-wizard.store';

const breadcrumbStore = useBreadcrumbStore();

const { items, isLoading: isBreadcrumbLoading } = storeToRefs(breadcrumbStore);

const route = useRoute();

const showGlobalBreadcrumb = computed(() => {
  const path = router.currentRoute.value.fullPath;

  return (
    (path.includes('projects') &&
      route.name !== 'boq' &&
      route.name !== 'no-projects-or-sites' &&
      route.name !== 'boq-orders' &&
      route.name !== 'boq-claims' &&
      route.name !== 'boq-compliance' &&
      route.name !== 'boq-bank-draws') ||
    path.includes('accounts') ||
    route.name === 'vendors' ||
    route.name === 'orders' ||
    route.name === 'claims'
  );
});

const hideTopNavigation = computed(() => {
  return (
    route.name === 'projects' ||
    route.name === 'orders' ||
    route.name === 'returns' ||
    // The third tab of the same `OrdersLayout`; without it the strip renders EMPTY here (this
    // route is not in `showGlobalBreadcrumb` either), leaving a hairline and 8px of white above
    // the page header that its two sibling tabs do not have.
    route.name === 'bulk-orders' ||
    route.name === 'claims' ||
    // Every create-bulk-order wizard step. Same reason as `bulk-orders` above: none of them is in
    // `showGlobalBreadcrumb`, so without this the strip renders EMPTY over the wizard's own page
    // header — a hairline and 8px of white its `create-new-order` sibling does not have.
    BULK_ORDER_WIZARD_ROUTE_NAMES.includes(route.name as string) ||
    route.name === 'create-new-order' ||
    route.name === 'create-new-claim' ||
    route.name === 'confirm-claim-requests' ||
    route.name === 'confirm-order-requests' ||
    route.name === 'create-custom-vendor' ||
    route.name === 'create-project' ||
    route.name === 'vendor-items' ||
    route.name === 'add-vendor-item' ||
    route.name === 'boq-admin-vendor-items' ||
    route.name === 'boq-admin-vendors' ||
    route.name === 'create-boq-vendor' ||
    route.name === 'boq-admin-add-vendor-item' ||
    route.name === 'vendors' ||
    route.name === 'organisation-details' ||
    route.name === 'role-capabilities'
  );
});

const getBreadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const storeItems = [...items.value];

  if (route.name === 'create-client-variation') {
    storeItems.push({
      label: 'Create client variation',
      to: '',
      index: storeItems.length,
      isClickable: false,
    });
  }

  if (route.name === 'variations') {
    storeItems.push({ label: 'Variations', to: '', index: storeItems.length, isClickable: false });
  }

  return storeItems.map((item, arrayIndex) => ({
    index: item.index,
    label: item.label,
    isCurrent: isCurrentCrumb(item, arrayIndex, storeItems.length),
    isClickable: item.isClickable,
  }));
});

const navigateBreadcrumb = (item: BreadcrumbItem) => {
  const storeItem = items.value.find((b) => b.index === item.index);
  if (!storeItem?.to) {
    return;
  }

  router.push(storeItem.to);
  items.value.splice(storeItem.index + 1);
};
</script>

<template>
  <div class="flex h-full flex-col overflow-hidden sm:h-dvh">
    <!-- Sticky header -->
    <div class="border-border-border-200 sticky top-0 z-20 border-b bg-white">
      <!-- Context: matches BoqHeader's strip spacing and rule, so the breadcrumb doesn't jump between the sites list and a BOQ (PLE-1778). -->
      <div
        v-if="!hideTopNavigation"
        class="border-border-border-200 base-horizontal-layout-spacing flex items-center border-b py-1"
      >
        <div class="min-w-0 flex-1 overflow-hidden">
          <Breadcrumb
            v-if="showGlobalBreadcrumb"
            class="w-full sm:w-auto"
            :items="getBreadcrumbItems"
            :is-loading="isBreadcrumbLoading"
            @item-clicked="navigateBreadcrumb"
          />
        </div>
      </div>
      <slot v-if="$slots.header" name="header" />
    </div>

    <!-- Scrollable content area -->
    <main class="bg-background-light-200 flex min-h-0 flex-1 flex-col overflow-y-auto">
      <slot name="content" />
    </main>

    <!-- Sticky footer -->
    <div
      v-if="$slots.footer"
      class="border-border-border-200 sticky bottom-0 z-20 border-t bg-white py-2"
    >
      <slot name="footer" />
    </div>
  </div>
</template>
